import {
  IconAlertTriangle,
  IconBuildingWarehouse,
  IconCalendarX,
  IconCheck,
  IconCircleX,
  IconFileInvoice,
  IconHandGrab,
  IconInbox,
  IconPackage,
  IconReceipt,
  IconTrolley,
  IconTruckDelivery,
  IconX,
} from '@tabler/icons-react';
import {
  commonStatusStepDefaults,
  CommonStatusSteps,
} from 'constants/statusConstants';
import { QueryParams } from 'types/api';
import { BomSchema } from 'types/clientV2/boms';
import { ProgramSchema } from 'types/clientV2/programs';
import { ShipmentSchema } from 'types/clientV2/shipments';
import {
  ShipmentStatus,
  StockLotSchemaClientV2,
  StockLotStatus,
} from 'types/inventory';
import { KitRequestSchema, KitRequestStatus } from 'types/kitting';
import { StatusStepGetter } from 'types/pizzaGraph';
import {
  PurchaseOrder,
  PurchaseOrderStatus,
  purchaseOrderStatusLabels,
  SupplierBillSchema,
  SupplierBillStatus,
  supplierBillStatusLabels,
} from 'types/procurement';
import { ProductionRunStatusOptions } from 'types/production';
import {
  RfqSchema,
  RfqStatus,
  rfqStatusLabels,
  SupplierQuoteSchema,
  SupplierQuoteStatus,
  supplierQuoteStatusLabels,
} from 'types/sourcing';
import { BomUnifiedSchema } from 'types/unified/boms';
import { ChipColor } from 'ui-component/extended/Chip';
import { isBeforeNowDate } from 'utils/dates';

export const bomStatusSchema: QueryParams<BomSchema>['schema'] = [
  'id',
  'createdAt',
  'approvedAt',
];
export const getBomStatusSteps: StatusStepGetter<BomSchema> = (
  record: BomSchema
) => [
  {
    ...commonStatusStepDefaults[CommonStatusSteps.DRAFT],
    completed: true,
    actualDate: record.createdAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.APPROVED],
    completed: !!record.approvedAt,
    actualDate: record.approvedAt,
  },
];

export const getUnifiedBomStatusSteps: StatusStepGetter<BomUnifiedSchema> = (
  record: BomUnifiedSchema
) => [
  {
    ...commonStatusStepDefaults[CommonStatusSteps.DRAFT],
    completed: true,
    actualDate: record.createdAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.APPROVED],
    completed: !!record.approvedAt,
    actualDate: record.approvedAt,
  },
];

export const rfqStatusSchema: QueryParams<RfqSchema>['schema'] = [
  'id',
  'createdAt',
  'approvedAt',
  'closedAt',
  'sentAt',
  'voidedAt',
];
export const getRfqStatusSteps: StatusStepGetter<RfqSchema> = (
  record: RfqSchema
) => [
  {
    ...commonStatusStepDefaults[CommonStatusSteps.DRAFT],
    completed: true,
    actualDate: record.createdAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.APPROVED],
    completed: !!record.approvedAt,
    actualDate: record.approvedAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.SENT],
    completed: !!record.sentAt,
    actualDate: record.sentAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.VOIDED],
    completed: !!record.voidedAt,
    actualDate: record.voidedAt,
    hidden: !record.voidedAt,
  },
  {
    key: RfqStatus.CLOSED,
    label: rfqStatusLabels[RfqStatus.CLOSED],
    icon: IconCircleX,
    color: record.closedAt ? ChipColor.warning : undefined,
    hidden: !record.closedAt,
    completed: !!record.closedAt,
    actualDate: record.closedAt,
    infoDescriptionComplete:
      'This RFQ has been closed, so new quotes cannot be submitted for it.',
    infoDescriptionIncomplete:
      'Once this RFQ is closed, no new quotes can be submitted for it.',
  },
];

export const supplierQuoteStatusSchema: QueryParams<SupplierQuoteSchema>['schema'] =
  ['id', 'createdAt', 'receivedAt', 'approvedAt', 'voidedAt', 'expiresAt'];
export const getSupplierQuoteStatusSteps: StatusStepGetter<
  SupplierQuoteSchema
> = (record: SupplierQuoteSchema) => [
  {
    key: SupplierQuoteStatus.RECEIVED,
    label: supplierQuoteStatusLabels[SupplierQuoteStatus.RECEIVED],
    icon: IconInbox,
    color: ChipColor.secondary,
    completed: true,
    actualDate: record.receivedAt ?? record.createdAt,
    infoDescriptionComplete: 'This quote has been received from the supplier.',
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.APPROVED],
    completed: !!record.approvedAt,
    actualDate: record.approvedAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.VOIDED],
    completed: !!record.voidedAt,
    actualDate: record.voidedAt,
    hidden: !record.voidedAt,
  },
  {
    key: SupplierQuoteStatus.EXPIRED,
    label:
      record.expiresAt && isBeforeNowDate(record.expiresAt)
        ? 'Expired'
        : 'Expires',
    icon: IconCalendarX,
    color:
      !!record.expiresAt && isBeforeNowDate(record.expiresAt)
        ? ChipColor.error
        : undefined,
    hidden: !record.expiresAt,
    completed: !!record.expiresAt && isBeforeNowDate(record.expiresAt),
    actualDate: record.expiresAt,
    infoDescriptionComplete: 'This quote has expired.',
    infoDescriptionIncomplete: 'This quote has not expired.',
  },
];

export const supplierBillStatusSchema: QueryParams<SupplierBillSchema>['schema'] =
  ['id', 'createdAt', 'receivedAt', 'approvedAt', 'paidAt', 'voidedAt'];
export const getSupplierBillStatusSteps: StatusStepGetter<
  SupplierBillSchema
> = (record: SupplierBillSchema) => [
  {
    key: SupplierQuoteStatus.RECEIVED,
    label: supplierQuoteStatusLabels[SupplierQuoteStatus.RECEIVED],
    icon: IconInbox,
    color: ChipColor.secondary,
    completed: true,
    actualDate: record.receivedAt ?? record.createdAt,
    infoDescriptionComplete: 'This bill has been received from the supplier.',
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.APPROVED],
    completed: !!record.approvedAt,
    actualDate: record.approvedAt,
  },
  {
    key: SupplierBillStatus.PAID,
    label: supplierBillStatusLabels[SupplierBillStatus.PAID],
    icon: IconReceipt,
    completed: !!record.paidAt,
    actualDate: record.paidAt,
    infoDescriptionComplete: 'This bill has been paid in full.',
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.VOIDED],
    completed: !!record.voidedAt,
    actualDate: record.voidedAt,
    hidden: !record.voidedAt,
  },
];

export const getStockLotStatusSteps: StatusStepGetter<
  StockLotSchemaClientV2
> = (record: StockLotSchemaClientV2) => [
  {
    key: StockLotStatus.EXPECTED,
    label: 'Expected',
    icon: IconTruckDelivery,
    color: ChipColor.secondary,
    completed: record.status === StockLotStatus.EXPECTED,
    hidden: record.status !== StockLotStatus.EXPECTED,
    infoDescriptionComplete:
      'This stock lot has been ordered but not yet received.',
  },
  {
    key: StockLotStatus.ON_HAND,
    label: 'On Hand',
    icon: IconBuildingWarehouse,
    completed: record.status === StockLotStatus.ON_HAND,
    infoDescriptionComplete: 'This stock lot has been received and is on hand.',
  },
  {
    key: StockLotStatus.QUARANTINED,
    label: 'Quarantined',
    icon: IconAlertTriangle,
    color: ChipColor.error,
    completed: record.quarantined,
    hidden: !record.quarantined,
    infoDescriptionComplete:
      'This stock lot has been marked as quarantined, so it cannot be used in kits, shipments, etc.',
  },
  {
    key: StockLotStatus.SMT_NO_PNP,
    label: 'No PNP SMT Part',
    icon: IconHandGrab,
    color: ChipColor.warning,
    completed: record.smtNoPnp,
    hidden: !record.smtNoPnp,
    infoDescriptionComplete:
      'This stock must be handled as a hand placed item and cannot be put into a PNP machine.',
  },
  {
    key: StockLotStatus.NO_STOCK,
    label: 'No Stock',
    icon: IconX,
    color: ChipColor.grey,
    completed: record.quant <= 0,
    hidden: record.quant > 0,
    infoDescriptionComplete:
      'This stock lot has been consumed, shipped, or otherwise deleted/trashed.',
  },
];

export const shipmentStatusSchema: QueryParams<ShipmentSchema>['schema'] = [
  'id',
  'shippedAt',
  'receivedAt',
  'ingestedAt',
];
export const getShipmentStatusSteps: StatusStepGetter<ShipmentSchema> = (
  record: ShipmentSchema
) => [
  {
    key: ShipmentStatus.SHIPPED,
    label: 'Shipped',
    icon: IconTruckDelivery,
    completed: !!record.shippedAt,
    actualDate: record.shippedAt,
    infoDescriptionComplete: 'This shipment has been shipped.',
  },
  {
    key: ShipmentStatus.RECEIVED,
    label: 'Delivered',
    icon: IconTrolley,
    completed: !!record.receivedAt,
    actualDate: record.receivedAt,
    infoDescriptionComplete:
      'This shipment has been been marked as delivered by the courier.',
    infoDescriptionIncomplete:
      'This shipment has not been been marked as delivered by the courier.',
  },
  {
    key: ShipmentStatus.INGESTED,
    label: 'Ingested',
    icon: IconPackage,
    completed: !!record.ingestedAt,
    actualDate: record.ingestedAt,
    infoDescriptionComplete:
      'This shipment has been delivered and the stock lots have been received and added to your inventory.',
    infoDescriptionIncomplete:
      'The contents of this shipment have not been fully received and added to your inventory.',
  },
];

export const kitRequestStatusSchema: QueryParams<KitRequestSchema>['schema'] = [
  'id',
  'createdAt',
  'approvedAt',
  'fulfilledAt',
  'voidedAt',
];
export const getKitRequestStatusSteps: StatusStepGetter<KitRequestSchema> = (
  record: KitRequestSchema
) => [
  {
    ...commonStatusStepDefaults[CommonStatusSteps.DRAFT],
    completed: true,
    actualDate: record.createdAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.APPROVED],
    completed: !!record.approvedAt,
    actualDate: record.approvedAt,
  },
  {
    key: KitRequestStatus.FULFILLED,
    label: 'Fulfilled',
    icon: IconTruckDelivery,
    completed: !!record.fulfilledAt,
    actualDate: record.fulfilledAt,
    infoDescriptionComplete:
      'All of the lines in this kit have been fulfilled.',
    infoDescriptionIncomplete:
      'One or more of the lines in this kit have not been fulfilled.',
  },
  {
    key: KitRequestStatus.VOIDED,
    label: 'Canceled',
    icon: IconCircleX,
    color: ChipColor.error,
    completed: !!record.voidedAt,
    actualDate: record.voidedAt,
    hidden: !record.voidedAt,
    infoDescriptionComplete: 'This kit has been canceled.',
  },
];

export const purchaseOrderStatusSchema: QueryParams<PurchaseOrder>['schema'] = [
  'id',
  'createdAt',
  'approvedAt',
  'sentAt',
  'fulfilledAt',
  'closedAt',
  'voidedAt',
];
export const getPurchaseOrderStatusSteps: StatusStepGetter<PurchaseOrder> = (
  record: PurchaseOrder
) => [
  {
    ...commonStatusStepDefaults[CommonStatusSteps.DRAFT],
    completed: true,
    actualDate: record.createdAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.APPROVED],
    completed: !!record.approvedAt,
    actualDate: record.approvedAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.SENT],
    completed: !!record.sentAt,
    actualDate: record.sentAt,
  },
  {
    key: PurchaseOrderStatus.FULFILLED,
    label: purchaseOrderStatusLabels[PurchaseOrderStatus.FULFILLED],
    icon: IconPackage,
    completed: !!record.fulfilledAt,
    actualDate: record.fulfilledAt,
    infoDescriptionComplete:
      'All of the lines in this purchase order have been received.',
    infoDescriptionIncomplete:
      'One or more of the lines in this purchase order have not been received.',
  },
  {
    key: PurchaseOrderStatus.CLOSED,
    label: purchaseOrderStatusLabels[PurchaseOrderStatus.CLOSED],
    icon: IconFileInvoice,
    completed: !!record.closedAt,
    actualDate: record.closedAt,
    infoDescriptionComplete:
      'This purchase order has been closed and no further supplier bills can be applied to it.',
    infoDescriptionIncomplete:
      'Once this purchase order is closed, no further supplier bills can be applied to it.',
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.VOIDED],
    completed: !!record.voidedAt,
    actualDate: record.voidedAt,
    hidden: !record.voidedAt,
  },
];

export const productionRunStatusSchema: QueryParams<ProgramSchema>['schema'] = [
  'id',
  'createdAt',
  'approvedAt',
  'executedAt',
];
export const getProductionRunStatusSteps: StatusStepGetter<ProgramSchema> = (
  record: ProgramSchema
) => [
  {
    ...commonStatusStepDefaults[CommonStatusSteps.DRAFT],
    completed: true,
    actualDate: record.createdAt,
  },
  {
    ...commonStatusStepDefaults[CommonStatusSteps.APPROVED],
    completed: !!record.approvedAt,
    actualDate: record.approvedAt,
  },
  {
    key: ProductionRunStatusOptions.complete,
    label: 'Complete',
    icon: IconCheck,
    completed: !!record.executedAt,
    actualDate: record.executedAt,
    infoDescriptionComplete: 'This program has been completed.',
    infoDescriptionIncomplete: 'This program has not been completed.',
  },
];
